<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { toast } from 'vue-sonner'

import appEmbed1 from '@/assets/pics/app-embed-1.png'
import appEmbed2 from '@/assets/pics/app-embed-2.png'
import { Icon } from '@/components/icon'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { useAmplitude } from '@/composables/use-amplitude'
import OnboardingRetentionLayout from '@/pages/onboarding-retention/components/OnboardingRetentionLayout.vue'
import { useCheckAppEmbedStatus } from '@/services/api/user.api'
import { useOnboardingRetentionProgressStore } from '@/stores/onboarding-retention-progress'
import { useUserStore } from '@/stores/user'

definePage({
  path: '/onboarding-retention/step3',
  meta: { layout: false },
})

const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const { track } = useAmplitude()
const STEP4_BACK_TARGET_KEY = 'retention-onboarding-step4-back-target'
const STEP4_ENTRY_MODE_KEY = 'retention-onboarding-step4-entry-mode'

function setStep4EntryState(mode: string, backTarget: string) {
  if (typeof window === 'undefined')
    return
  window.sessionStorage.setItem(STEP4_ENTRY_MODE_KEY, mode)
  window.sessionStorage.setItem(STEP4_BACK_TARGET_KEY, backTarget)
}

const userStore = useUserStore()
const progressStore = useOnboardingRetentionProgressStore()
const { progress, loaded, resolvedEsp, selectedEspMeta } = storeToRefs(progressStore)
const i18nParams = computed(() => ({ esp: selectedEspMeta.value?.displayName ?? 'Klaviyo' }))

const { mutateAsync: checkAppEmbed } = useCheckAppEmbedStatus()

const isChecking = ref(false)
const appEmbedEnabled = ref(false)
const hasChecked = ref(false)
const saving = ref(false)

const progressData = computed<Record<string, any>>(() => progress.value?.progress_data || {})

const isShopify = computed(() => userStore.user?.team?.shop_type === 'shopify')
const shopifyThemeAppEmbedId = import.meta.env.VITE_SHOPIFY_THEME_APP_EMBED_ID || '23bfde94-0160-4a8f-acfc-12821b623625'
const shopifyEnableUrl = computed(() => {
  const rawShop = userStore.user?.team?.shop || ''
  const shop = rawShop.replace(/^https?:\/\//, '').replace(/\/.*$/, '').replace(/\/$/, '')
  if (!shop)
    return '#'

  let storeHandle = shop
  if (shop.includes('.myshopify.com')) {
    storeHandle = shop.split('.myshopify.com')[0]
  }
  else if (shop.includes('.')) {
    const parts = shop.split('.').filter(Boolean)
    if (parts.length >= 2 && (parts[0] === 'www' || parts[0] === 'shop'))
      storeHandle = parts[1]
    else storeHandle = parts[0] || shop
  }

  if (!storeHandle)
    return '#'

  return `https://admin.shopify.com/store/${storeHandle}/themes/current/editor?context=apps&activateAppId=${shopifyThemeAppEmbedId}/app-embed`
})

const completedSteps = computed(() => {
  const out: number[] = []
  if (progressData.value.step1Connected === true)
    out.push(1)
  if (progressData.value.step2ConversionFeedConfigured === true)
    out.push(2)
  if (appEmbedEnabled.value || progressData.value.step3AppEmbedEnabled === true)
    out.push(3)
  if (progress.value?.is_completed)
    out.push(4)
  return out
})

async function ensureLoaded() {
  if (loaded.value)
    return
  let result: any = null
  try {
    result = await progressStore.loadFromBackend()
  }
  catch (e) {
    console.warn('[retention-onboarding step3] loadFromBackend failed, fallback local:', e)
  }
  if (!resolvedEsp.value) {
    if (result !== null) {
      router.replace('/onboarding-retention/step0')
      return
    }
    progressStore.setEsp('klaviyo')
    return
  }
  if (result === 'completed') {
    router.replace('/onboarding-retention/step4')
  }
}

async function saveProgress(nextStep: number, patch: Record<string, any>) {
  const merged = {
    ...(progress.value?.progress_data || {}),
    ...patch,
  }
  await progressStore.save({
    current_step: nextStep,
    current_platform: 'retention',
    progress_data: merged,
  })
  if (progress.value) {
    progress.value = {
      ...progress.value,
      current_step: nextStep,
      current_platform: 'retention',
      progress_data: merged,
    }
  }
}

async function checkEmbedStatus() {
  if (isChecking.value)
    return
  isChecking.value = true
  hasChecked.value = true

  try {
    const result = await checkAppEmbed()
    appEmbedEnabled.value = result.enabled
    if (result.enabled)
      toast.success(t('onboardingRetention.step3.toasts.enabled'))
    else toast.warning(t('onboardingRetention.step3.toasts.notEnabled'))
  }
  catch {
    toast.error(t('onboardingRetention.step3.toasts.checkFailed'))
    appEmbedEnabled.value = false
  }
  finally {
    isChecking.value = false
  }
}

function handleIHaveDoneToggle(value: boolean) {
  if (value)
    track('retention_onboarding_step3_ive_enabled_it_click')
  hasChecked.value = true
  appEmbedEnabled.value = value
  if (value)
    toast.success(t('onboardingRetention.step3.toasts.ready'))
}

async function next() {
  if (saving.value)
    return
  track('retention_onboarding_step3_continue_click', { esp: resolvedEsp.value })
  saving.value = true
  try {
    const enabled = !isShopify.value ? true : appEmbedEnabled.value
    if (!enabled)
      return
    setStep4EntryState('normal-step3', '/onboarding-retention/step3')
    await saveProgress(4, { step3AppEmbedEnabled: true })
    router.push({ path: '/onboarding-retention/step4', query: { prev: route.fullPath } })
  }
  finally {
    saving.value = false
  }
}

function back() {
  track('retention_onboarding_step3_back_click')
  const prev = route.query?.prev as string | undefined
  if (prev)
    router.push(prev)
  else router.push('/onboarding-retention/step2')
}

onMounted(async () => {
  track('retention_onboarding_step3_pageview', { esp: resolvedEsp.value })
  await ensureLoaded()

  if (!isShopify.value) {
    hasChecked.value = true
    appEmbedEnabled.value = true
    return
  }

  if (progressData.value.step3AppEmbedEnabled === true) {
    hasChecked.value = true
    appEmbedEnabled.value = true
    return
  }

  await checkEmbedStatus()
})
</script>

<template>
  <OnboardingRetentionLayout
    :current-step="3"
    :completed-steps="completedSteps"
    title-key="onboardingRetention.step3.title"
    subtitle-key="onboardingRetention.step3.subtitle"
  >
    <div class="space-y-4">
      <div class="rounded-[var(--p-border-radius-200)] bg-white border border-black overflow-hidden">
        <div class="p-6">
          <div class="flex items-center justify-between gap-3">
            <div class="text-sm font-semibold text-slate-900">
              {{ t('onboardingRetention.step3.panelTitle') }}
            </div>
          </div>
          <div class="mt-2 text-sm text-[#6D6D6D] leading-snug">
            <span class="mr-1">1.</span>
            <a
              :href="shopifyEnableUrl"
              target="_blank"
              rel="noopener noreferrer"
              :class="shopifyEnableUrl === '#'
                ? 'text-[#6D6D6D] underline cursor-not-allowed pointer-events-none'
                : 'text-[#28704C] underline hover:underline underline-offset-2 cursor-pointer'"
            >
              {{ t('onboardingRetention.step3.panelStep1Link') }}
            </a>
            <span>{{ t('onboardingRetention.step3.panelStep1Rest') }}</span>
          </div>
          <div class="mt-4">
            <img :src="appEmbed1" :alt="t('onboardingRetention.step3.appEmbedImageAlt1')" class="w-full rounded-[var(--p-border-radius-200)]">
          </div>
        </div>

        <div class="p-6 space-y-4 pt-0">
          <div class="text-sm text-[#6D6D6D] leading-snug">
            <span class="mr-1">2.</span>
            <span>{{ t('onboardingRetention.step3.toggleConfirmHint') }}</span>
          </div>

          <div class="flex items-center justify-between gap-3">
            <div class="flex items-center gap-3">
              <Switch :model-value="appEmbedEnabled" :disabled="isChecking" @update:model-value="handleIHaveDoneToggle" />
              <div class="text-sm font-semibold text-slate-900">
                {{ t('onboardingRetention.step3.iHaveDone') }}
              </div>
            </div>
            <Button variant="outline" class="border-current text-[#6D6D6D] shadow-none hover:bg-white" :disabled="isChecking" @click="checkEmbedStatus">
              <Icon name="lucide:refresh-cw" class="h-4 w-4 mr-2" />
              {{ t('onboardingRetention.step3.checkButton') }}
            </Button>
          </div>

          <div v-if="hasChecked" class="space-y-2">
            <div v-if="appEmbedEnabled" class="flex items-center gap-2 text-sm bg-[#DFECE5] rounded-[var(--p-border-radius-200)] px-3 py-2 text-[#28704C]">
              <Icon name="lucide:check-circle-2" class="w-4 h-4 text-[#28704C]" />
              <span>{{ t('onboardingRetention.step3.statusEnabled') }}</span>
            </div>
            <div v-else class="flex items-center gap-2 text-sm bg-[#FAE3A4] rounded-[var(--p-border-radius-200)] px-3 py-2 text-[#69500C] border-0">
              <Icon name="lucide:alert-triangle" class="w-4 h-4 text-[#69500C]" />
              <span>{{ t('onboardingRetention.step3.statusNotEnabled') }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="flex items-center justify-between gap-3 pt-2">
        <Button variant="outline" class="bg-white border-black text-black shadow-none hover:bg-white" @click="back">
          {{ t('onboardingRetention.actions.back') }}
        </Button>
        <Button
          :disabled="saving || !appEmbedEnabled"
          :class="(!saving && appEmbedEnabled) ? 'bg-black hover:bg-black/90 shadow-none' : 'bg-[#6D6D6D] hover:bg-[#5d5d5d] shadow-none'"
          @click="next"
        >
          <Icon v-if="saving" name="lucide:loader-2" class="h-4 w-4 mr-2 animate-spin" />
          {{ t('onboardingRetention.actions.continue') }}
        </Button>
      </div>
    </div>

    <template #right>
      <div class="h-full w-full bg-[#F5F4ED]">
        <div class="px-8 pt-8 pb-8">
          <div class="text-[10px] font-semibold tracking-[0.18em] text-[#6D6D6D]">
            {{ t('onboardingRetention.step3.valueCard.withoutTitle') }}
          </div>
          <div class="mt-3 rounded-[var(--p-border-radius-300)] bg-[#F2D8D8] px-6 py-6">
            <div class="space-y-4 text-[14px] leading-snug text-[#8F2C2C]">
              <div class="flex items-start gap-3">
                <Icon name="lucide:x" class="h-4 w-4 mt-0.5" />
                <span>{{ t('onboardingRetention.step3.valueCard.withoutPoint1') }}</span>
              </div>
              <div class="flex items-start gap-3">
                <Icon name="lucide:x" class="h-4 w-4 mt-0.5" />
                <span>{{ t('onboardingRetention.step3.valueCard.withoutPoint2', i18nParams) }}</span>
              </div>
            </div>
          </div>

          <div class="py-4 flex justify-center text-[#C7C7C7]">
            <Icon name="lucide:arrow-down" class="h-7 w-7" />
          </div>

          <div class="text-[10px] font-semibold tracking-[0.18em] text-[#6D6D6D]">
            {{ t('onboardingRetention.step3.valueCard.withTitle') }}
          </div>
          <div class="mt-3 rounded-[var(--p-border-radius-300)] bg-[#DCEAD9] px-6 py-6">
            <div class="space-y-4 text-[14px] leading-snug text-[#1F5B3A]">
              <div class="flex items-start gap-3">
                <Icon name="lucide:check" class="h-4 w-4 mt-0.5" />
                <span>{{ t('onboardingRetention.step3.valueCard.withPoint1') }}</span>
              </div>
              <div class="flex items-start gap-3">
                <Icon name="lucide:check" class="h-4 w-4 mt-0.5" />
                <span>{{ t('onboardingRetention.step3.valueCard.withPoint2') }}</span>
              </div>
            </div>
          </div>

          <div class="pt-10 space-y-3">
            <div class="text-[15px] text-[#6D6D6D] leading-snug">
              {{ t('onboardingRetention.step3.warningText') }}
            </div>
            <img :src="appEmbed2" :alt="t('onboardingRetention.step3.appEmbedImageAlt2')" class="w-[80%] rounded-[var(--p-border-radius-200)]">
          </div>
        </div>
      </div>
    </template>
  </OnboardingRetentionLayout>
</template>
